import {
  Autocomplete,
  createFilterOptions,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'

export type SuggestionOption = {
  code: string
  name: string
}

type SuggestionAutocompleteFieldProps = {
  name: string
  label: string
  options: SuggestionOption[]
  transform?: (value: string) => string
  maxLength?: number
}

const filterOptions = createFilterOptions<SuggestionOption>({
  stringify: (option) => `${option.name} ${option.code}`,
})

const SuggestionAutocompleteField = ({
  name,
  label,
  options,
  transform,
  maxLength,
}: SuggestionAutocompleteFieldProps) => {
  const { control } = useFormContext()

  return (
    <Stack spacing={1}>
      <InputLabel sx={{ color: 'secondary.dark', fontWeight: 300 }}>
        {label}
      </InputLabel>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const selected =
            options.find((option) => option.code === value) ?? null

          return (
            <Autocomplete
              freeSolo
              forcePopupIcon
              openOnFocus
              autoHighlight
              options={options}
              filterOptions={filterOptions}
              value={selected}
              inputValue={value || ''}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.code
              }
              isOptionEqualToValue={(option, selectedValue) =>
                option.code === selectedValue.code
              }
              onInputChange={(_, inputValue, reason) => {
                if (reason === 'reset') return
                const next = transform ? transform(inputValue) : inputValue
                onChange(next)
              }}
              onChange={(_, option) => {
                if (!option) {
                  onChange('')
                  return
                }
                if (typeof option === 'string') {
                  onChange(transform ? transform(option) : option)
                  return
                }
                onChange(option.code)
              }}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 20,
                  },
                },
                listbox: {
                  sx: { maxHeight: 280 },
                },
              }}
              renderOption={(props, option) => (
                <li {...props} key={`${option.code}-${option.name}`}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.code}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{
                    ...params.inputProps,
                    maxLength,
                    autoComplete: 'off',
                  }}
                />
              )}
            />
          )
        }}
      />
    </Stack>
  )
}

export default SuggestionAutocompleteField
